"use client"

import { X, Check, Zap, Users, Building2, Lock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useUserStore, type UserTier } from "@/lib/stores/user-store"

type UpgradeTrigger =
  | "chat_limit"
  | "goal_planning"
  | "advanced_dashboard"
  | "team_invite"
  | "multiple_orgs"
  | "section_access"

interface UpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  trigger: UpgradeTrigger
  targetTier?: UserTier
}

const TIER_INFO = {
  starter: {
    name: "Starter",
    price: "$29",
    period: "month",
    icon: Zap,
    features: [
      "Unlimited AI chat messages",
      "All 12 business sections",
      "Full RAG across all categories",
      "Strategic insights & dashboards",
      "Goal creation & tracking",
      "Email support",
    ],
  },
  team: {
    name: "Team",
    price: "$79",
    period: "month",
    icon: Users,
    features: [
      "Everything in Starter",
      "Up to 10 team members",
      "Shared dashboards & insights",
      "Task assignment & tracking",
      "Team performance analytics",
      "Role-based permissions",
      "Priority email support",
    ],
  },
  agency: {
    name: "Agency",
    price: "$199",
    period: "month",
    icon: Building2,
    features: [
      "Everything in Team",
      "Unlimited client workspaces",
      "Client portal & sharing",
      "Portfolio dashboard",
      "Branded client reports",
      "API access (100K calls/month)",
      "Priority chat support",
    ],
  },
}

const TRIGGER_MESSAGES: Record<UpgradeTrigger, { title: string; description: string; targetTier: UserTier }> = {
  chat_limit: {
    title: "Daily Chat Limit Reached",
    description: "You've used all 25 free messages today. Chat resets at midnight.",
    targetTier: "starter",
  },
  section_access: {
    title: "Unlock All Business Sections",
    description:
      "Access Products, Services, Audience, Brand, Competition, and 7 more categories to build your complete business profile.",
    targetTier: "starter",
  },
  goal_planning: {
    title: "Goal Planning Requires Starter Plan",
    description: "Track progress toward your business goals with powerful goal planning tools.",
    targetTier: "starter",
  },
  advanced_dashboard: {
    title: "Advanced Dashboards Require Starter Plan",
    description: "Access Marketing, Sales, Operations, and Finance dashboard agents.",
    targetTier: "starter",
  },
  team_invite: {
    title: "Team Collaboration Requires Team Plan",
    description: "Invite team members and collaborate on your business intelligence.",
    targetTier: "team",
  },
  multiple_orgs: {
    title: "Multiple Clients Requires Agency Plan",
    description: "Manage unlimited client workspaces from a single dashboard.",
    targetTier: "agency",
  },
}

export function UpgradeModal({ isOpen, onClose, trigger, targetTier }: UpgradeModalProps) {
  const { upgrade } = useUserStore()

  if (!isOpen) return null

  const triggerInfo = TRIGGER_MESSAGES[trigger]
  const tier = targetTier || triggerInfo.targetTier
  const tierInfo = TIER_INFO[tier as keyof typeof TIER_INFO]

  if (!tierInfo) return null

  const Icon = trigger === "section_access" ? Lock : tierInfo.icon

  const handleStartTrial = () => {
    upgrade(tier)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md rounded-2xl bg-background border border-[var(--border-light)] p-5 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface)] motion-fast"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="h-14 w-14 rounded-2xl bg-[var(--ai-light)] flex items-center justify-center">
              <Icon className="h-7 w-7 text-[var(--ai)]" strokeWidth={1.5} />
            </div>
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">{triggerInfo.title}</h2>
          <p className="text-body sm:text-lg text-[var(--text-secondary)]">{triggerInfo.description}</p>
        </div>

        {/* Features list with better mobile spacing */}
        <div className="bg-[var(--surface)] border border-[var(--border-light)] rounded-xl p-4 sm:p-5 mb-5 sm:mb-6">
          <div className="flex items-baseline justify-between mb-3 sm:mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-foreground">{tierInfo.name} Plan</h3>
            <div>
              <span className="text-xl sm:text-2xl font-bold text-foreground">{tierInfo.price}</span>
              <span className="text-sm sm:text-body text-[var(--text-secondary)]">/{tierInfo.period}</span>
            </div>
          </div>

          <div className="space-y-2.5 sm:space-y-3">
            {tierInfo.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-2.5 sm:gap-3">
                <div className="h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-[var(--success-light)] flex items-center justify-center shrink-0">
                  <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-[var(--success)]" />
                </div>
                <span className="text-sm sm:text-body text-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <Button onClick={handleStartTrial} className="w-full h-12 bg-[var(--cta)] hover:bg-[var(--cta-hover)] mb-3">
          Start 14-Day Free Trial
        </Button>

        <p className="text-center text-caption sm:text-body text-[var(--text-tertiary)] mb-4">
          No credit card required for trial
        </p>

        <div className="flex justify-center gap-4">
          <button
            onClick={onClose}
            className="text-caption sm:text-body text-[var(--text-secondary)] hover:text-foreground motion-fast"
          >
            Maybe Later
          </button>
          <span className="text-[var(--text-tertiary)]">•</span>
          <button className="text-caption sm:text-body text-[var(--cta)] hover:text-[var(--cta-hover)] motion-fast">
            Compare Plans
          </button>
        </div>
      </div>
    </div>
  )
}
